import type { DocumentNode } from 'graphql';

import type { PageInfo } from '@zen/types';
import { PageSize } from '@zen/types';

import { getNextPageVariables, getPreviousPageVariables } from './helpers';
import type { PaginatedQueryResult, PaginatedVariables, PaginationQueryHookVariables, UpdateQueryType } from './types';
import usePaginatedQuery from './usePaginatedQuery';

const usePagination = <Result, Variables extends PaginatedVariables, NodeType>(
  document: DocumentNode,
  responsePath: string,
  variables: PaginationQueryHookVariables<Variables>,
  limit: number = PageSize.TWENTY,
  options: object = {}
): PaginatedQueryResult<Result, Variables, NodeType> => {
  const { fetchMore, pageInfo, ...queryResults } = usePaginatedQuery<Result, Variables, NodeType>(
    document,
    responsePath,
    { ...variables, first: limit },
    options
  );

  const { startCursor, endCursor, hasNextPage, hasPreviousPage } = pageInfo;
  const updateQuery: UpdateQueryType<Result, Result> = (_previousQueryResult, { fetchMoreResult }) => fetchMoreResult;

  const handleNext = () =>
    fetchMore({
      variables: getNextPageVariables(limit, endCursor),
      updateQuery
    });

  // Stay on the same paginated page, but refresh results on that page
  const handlePageRefetch = () => {
    const pageHasOnlyOneItem: boolean = startCursor === endCursor;

    return fetchMore({
      variables: pageHasOnlyOneItem && !hasPreviousPage ? {} : getPreviousPageVariables(limit, endCursor),
      updateQuery
    });
  };

  const handlePrevious = () =>
    fetchMore({
      variables: getPreviousPageVariables(limit, startCursor),
      updateQuery
    });

  const paginationInfo: PageInfo = {
    hasNextPage,
    hasPreviousPage,
    onNext: handleNext,
    onPrevious: handlePrevious
  };

  return { ...queryResults, paginationInfo, pageRefresh: handlePageRefetch };
};

export default usePagination;
export type { PaginatedVariables };
